import { Menu, MenuList, useOutsideClick } from '@chakra-ui/react'
import { Token, Width } from 'types/chakra'
import { IAsyncSelectOption, IAsyncSelectOptionWithIcon } from 'interfaces/common'
import DropdownButton from './DropdownButton'
import DropdownSelection from './DropdownSelection'
import { uniqBy } from 'lodash'
import { useRef, useState, useEffect } from 'react'

interface IDropdownProps {
  name: string
  items: IAsyncSelectOptionWithIcon<number>[]
  options: IAsyncSelectOptionWithIcon<number>[]
  setItems: (options: IAsyncSelectOptionWithIcon<number>[]) => void
  width?: Token<Width | number, 'sizes'>
  height?: Token<Width | number, 'sizes'>
  maxWidth?: Token<Width | number, 'sizes'>
  placeHolder?: string
  isMatchWidth?: boolean
  isDisabled?: boolean
}

const Dropdown = (props: IDropdownProps) => {
  const { name, items, options, setItems, width, maxWidth, placeHolder, isMatchWidth, height, isDisabled } = props
  const [keyword, setKeyword] = useState<string>('')
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  const filteredOptions = options.filter(option => option?.label?.toLowerCase()?.includes(keyword.toLowerCase()))

  const sortedOptions = uniqBy([...items, ...filteredOptions], 'value')

  useOutsideClick({
    ref: menuButtonRef,
    handler: () => {
      setIsFocused(false)
      setKeyword('')
    }
  })

  const handleSelectOption = (option: IAsyncSelectOption<number>) => {
    setItems(
      items.some(item => item.value === option.value)
        ? items.filter(item => item.value !== option.value)
        : [...items, option]
    )
    setIsFocused(false) // Close dropdown after selection
  }

  useEffect(() => {
    if (isFocused) {
      setKeyword('')
    }
  }, [isFocused])

  return (
    <Menu closeOnSelect={true} autoSelect={false} computePositionOnMount matchWidth={isMatchWidth}>
      {({ isOpen }) => (
        <>
          <DropdownButton
            items={items}
            placeHolder={placeHolder}
            isOpen={isOpen}
            width={width}
            height={height}
            isDisabled={isDisabled}
            keyword={keyword}
            setKeyword={setKeyword}
            isFocused={isFocused}
            setIsFocused={setIsFocused}
            buttonRef={menuButtonRef}
          />
          <MenuList
            zIndex="1001"
            maxWidth={maxWidth}
            minWidth="auto"
            maxHeight="300px"
            overflowY="scroll"
            border="1px solid #E2E8F0"
            hidden={sortedOptions.length === 0}
            autoFocus={false}
          >
            {sortedOptions.map((option, index) => (
              <DropdownSelection
                key={`${name}-${index}`}
                item={option}
                isSelected={items.some(item => item.value === option.value)}
                onClick={() => handleSelectOption(option)}
                height={height}
                maxWidth={maxWidth}
              />
            ))}
          </MenuList>
        </>
      )}
    </Menu>
  )
}

export default Dropdown
